//CRN_20260508 - Riskline Alerts service (PUSH side - reads from SureSim backend; live updates via SignalR)
//CRN_20260513 - https://riskline.readme.io/reference/apis-overview

import * as signalR from '@microsoft/signalr';
import { AdvisoryDTO } from '../../models/system/riskline/riskline-advisory.dto';
import { AlertDTO, AlertFilterDTO, AlertPagedResultDTO } from '../../models/system/riskline/riskline-alert.dto';
import { catchError, map, Observable, of, Subject, Subscription, throwError, timer } from 'rxjs';
import { CityReportDTO } from '../../models/system/riskline/riskline-city-report.dto';
import { CountryReportDTO } from '../../models/system/riskline/riskline-country-report.dto';
import { CountryRiskLevelDTO } from '../../models/system/riskline/riskline-country-risk-level.dto';
import { DecryptAesClientSide } from '../../encryption';
import { environment } from 'src/environments/environment';
import { eSimApiPaths, StorageValues } from '../../consts';
import { ExceptionsService } from '../utelize/general/exceptions-service';
import { HttpClientService } from '../utelize/general/http-client-service';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PtaDTO } from '../../models/system/riskline/riskline-pta.dto';
import { SpecificTravellerAdviceDTO } from '../../models/system/riskline/riskline-sta.dto';
import { StorageService } from '../utelize/general/storage-service';
import { SummaryDTO } from '../../models/system/riskline/riskline-summary.dto';

export interface RisklineCompositeKeyDeletePayload {
  countryIsoCode: string;
  language      : string;
}

@Injectable({ providedIn: 'root' })
export class RisklineAlertsService {

  //#region Hub event subjects

  private advisoryDeletedSource         : Subject<string>                           =new Subject<string>();
  private advisoryReceivedSource        : Subject<AdvisoryDTO>                      =new Subject<AdvisoryDTO>();
  private advisoryUpdatedSource         : Subject<AdvisoryDTO>                      =new Subject<AdvisoryDTO>();
  private alertDeletedSource            : Subject<string>                           =new Subject<string>();
  private alertReceivedSource           : Subject<AlertDTO>                         =new Subject<AlertDTO>();
  private alertUpdatedSource            : Subject<AlertDTO>                         =new Subject<AlertDTO>();
  private cityReportDeletedSource       : Subject<string>                           =new Subject<string>();
  private cityReportReceivedSource      : Subject<CityReportDTO>                    =new Subject<CityReportDTO>();
  private countryReportDeletedSource    : Subject<RisklineCompositeKeyDeletePayload>=new Subject<RisklineCompositeKeyDeletePayload>();
  private countryReportReceivedSource   : Subject<CountryReportDTO>                 =new Subject<CountryReportDTO>();
  private countryRiskLevelDeletedSource : Subject<RisklineCompositeKeyDeletePayload>=new Subject<RisklineCompositeKeyDeletePayload>();
  private countryRiskLevelReceivedSource: Subject<CountryRiskLevelDTO>              =new Subject<CountryRiskLevelDTO>();
  private hubConnection                 : signalR.HubConnection | undefined;
  private isHubStarting                 : boolean                                   =false;
  private pollFallbackSub               : Subscription | undefined;
  private pollFallbackTickSource        : Subject<void>                             =new Subject<void>();
  private ptaDeletedSource              : Subject<RisklineCompositeKeyDeletePayload>=new Subject<RisklineCompositeKeyDeletePayload>();
  private ptaReceivedSource             : Subject<PtaDTO>                           =new Subject<PtaDTO>();
  private staDeletedSource              : Subject<string>                           =new Subject<string>();
  private staReceivedSource             : Subject<SpecificTravellerAdviceDTO>       =new Subject<SpecificTravellerAdviceDTO>();
  private summaryDeletedSource          : Subject<RisklineCompositeKeyDeletePayload>=new Subject<RisklineCompositeKeyDeletePayload>();
  private summaryReceivedSource         : Subject<SummaryDTO>                       =new Subject<SummaryDTO>();

  public readonly advisoryDeleted$         : Observable<string>                           =this.advisoryDeletedSource         .asObservable();
  public readonly advisoryReceived$        : Observable<AdvisoryDTO>                      =this.advisoryReceivedSource        .asObservable();
  public readonly advisoryUpdated$         : Observable<AdvisoryDTO>                      =this.advisoryUpdatedSource         .asObservable();
  public readonly alertDeleted$            : Observable<string>                           =this.alertDeletedSource            .asObservable();
  public readonly alertReceived$           : Observable<AlertDTO>                         =this.alertReceivedSource           .asObservable();
  public readonly alertUpdated$            : Observable<AlertDTO>                         =this.alertUpdatedSource            .asObservable();
  public readonly cityReportDeleted$       : Observable<string>                           =this.cityReportDeletedSource       .asObservable();
  public readonly cityReportReceived$      : Observable<CityReportDTO>                    =this.cityReportReceivedSource      .asObservable();
  public readonly countryReportDeleted$    : Observable<RisklineCompositeKeyDeletePayload>=this.countryReportDeletedSource    .asObservable();
  public readonly countryReportReceived$   : Observable<CountryReportDTO>                 =this.countryReportReceivedSource   .asObservable();
  public readonly countryRiskLevelDeleted$ : Observable<RisklineCompositeKeyDeletePayload>=this.countryRiskLevelDeletedSource .asObservable();
  public readonly countryRiskLevelReceived$: Observable<CountryRiskLevelDTO>              =this.countryRiskLevelReceivedSource.asObservable();
  public readonly pollFallbackTick$        : Observable<void>                             =this.pollFallbackTickSource        .asObservable();
  public readonly ptaDeleted$              : Observable<RisklineCompositeKeyDeletePayload>=this.ptaDeletedSource              .asObservable();
  public readonly ptaReceived$             : Observable<PtaDTO>                           =this.ptaReceivedSource             .asObservable();
  public readonly staDeleted$              : Observable<string>                           =this.staDeletedSource              .asObservable();
  public readonly staReceived$             : Observable<SpecificTravellerAdviceDTO>       =this.staReceivedSource             .asObservable();
  public readonly summaryDeleted$          : Observable<RisklineCompositeKeyDeletePayload>=this.summaryDeletedSource          .asObservable();
  public readonly summaryReceived$         : Observable<SummaryDTO>                       =this.summaryReceivedSource         .asObservable();

  //#endregion

  constructor(
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService,
    private storageService   : StorageService
  ) { }

  //#region REST

  public getActiveAlertsForCountry(isoCode: string): Observable<AlertDTO[]> {
    try {
      if (!isoCode || isoCode.trim()=='')
        throw new Error('RisklineAlertsService.getActiveAlertsForCountry() - isoCode is required');

      const params=new HttpParams()
                    .set('countryIsoCode', isoCode)
                    .set('isActive'      , 'true');

      return this.httpClientService
                 .get<AlertPagedResultDTO>(environment.eSimApi
                                           + eSimApiPaths.RiskIntelligence
                                           + '/'
                                           + eSimApiPaths.RiskIntelligenceAlerts,
                                           { params: params })
                 .pipe(
                   map(result => result?.items ?? []),
                   catchError((error) => {
                     return throwError(() => new Error('RisklineAlertsService.getActiveAlertsForCountry() - ' + this.exceptionsService.formatHttpError(error)));
                   })
                 );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  public getAlertById(id: string): Observable<AlertDTO> {
    try {
      if (!id || id.trim()=='')
        throw new Error('RisklineAlertsService.getAlertById() - id is required');

      return this.httpClientService
                 .get<AlertDTO>(environment.eSimApi
                                + eSimApiPaths.RiskIntelligence
                                + '/'
                                + eSimApiPaths.RiskIntelligenceAlerts
                                + '/'
                                + id)
                 .pipe(
                   catchError((error) => {
                     return throwError(() => new Error('RisklineAlertsService.getAlertById() - ' + this.exceptionsService.formatHttpError(error)));
                   })
                 );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getAlerts(filter: AlertFilterDTO): Observable<AlertPagedResultDTO> {
    try {
      let params=new HttpParams();

      if (filter.categoryIds?.length)
        filter.categoryIds.forEach(id => { params=params.append('categoryIds', id.toString()); });

      if (filter.countryIsoCode)
        params=params.set('countryIsoCode', filter.countryIsoCode);

      if (filter.endDate)
        params=params.set('endDate', filter.endDate);

      if (filter.isActive!=null)
        params=params.set('isActive', filter.isActive.toString());

      if (filter.pageIndex!=null)
        params=params.set('pageIndex', filter.pageIndex.toString());

      if (filter.pageSize!=null)
        params=params.set('pageSize', filter.pageSize.toString());

      if (filter.riskLevelIds?.length)
        filter.riskLevelIds.forEach(id => { params=params.append('riskLevelIds', id.toString()); });

      if (filter.sortBy)
        params=params.set('sortBy', filter.sortBy);

      if (filter.sortDirection)
        params=params.set('sortDirection' , filter.sortDirection);

      if (filter.startDate)
        params=params.set('startDate', filter.startDate);

      if (filter.takesPlace!=null)
        params=params.set('takesPlace', filter.takesPlace.toString());

      return this.httpClientService
             .get<AlertPagedResultDTO>(environment.eSimApi
                                       + eSimApiPaths.RiskIntelligence
                                       + '/'
                                       + eSimApiPaths.RiskIntelligenceAlerts,
                                       { params: params })
             .pipe(
               catchError((error) => {
                 return throwError(() => new Error('RisklineAlertsService.getAlerts() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of({ items: [], pageIndex: 0, pageSize: 0, totalCount: 0 });
    }
  }

  //#endregion

  //#region Poll fallback

  private startPollFallback(): void {
    try {
      if (this.pollFallbackSub!=undefined)
        return;

      const intervalMs: number=environment.risklineConfig.pollFallbackIntervalMs ?? 0;

      if (intervalMs<=0)
        return;

      this.pollFallbackSub=timer(intervalMs, intervalMs).subscribe({
        next: () => {
          if (this.isHubConnected())
            this.stopPollFallback();
          else
            this.pollFallbackTickSource.next();
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private stopPollFallback(): void {
    try {
      this.pollFallbackSub?.unsubscribe();
      this.pollFallbackSub=undefined;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region SignalR live updates

  public isHubConnected(): boolean {
    try {
      return this.hubConnection!=undefined && this.hubConnection.state==signalR.HubConnectionState.Connected;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public startHubConnection(): void {
    try {
      if (!environment.risklineConfig.enabled)
        return;

      if (this.hubConnection!=undefined && this.hubConnection.state==signalR.HubConnectionState.Connected)
        return;

      if (this.isHubStarting)
        return;

      this.isHubStarting=true;

      const token: string=DecryptAesClientSide(this.storageService.getItem(StorageValues.encToken)) ?? '';
      const url  : string=environment.eSimApiWithoutApiPath + environment.risklineConfig.alertSignalrHubPath;

      this.hubConnection=new signalR.HubConnectionBuilder()
                                    .withUrl(url, {
                                      accessTokenFactory: () => token,
                                      transport         : signalR.HttpTransportType.LongPolling |
                                                          signalR.HttpTransportType.ServerSentEvents |
                                                          signalR.HttpTransportType.WebSockets,
                                      withCredentials   : false
                                    })
                                    .withAutomaticReconnect()
                                    .configureLogging(signalR.LogLevel.Warning)
                                    .build();

      //CRN_20260511 - Alerts (with _Updated_ variant)
      this.hubConnection.on('AlertReceived', (alert: AlertDTO) => { this.alertReceivedSource.next(alert); });
      this.hubConnection.on('AlertUpdated' , (alert: AlertDTO) => { this.alertUpdatedSource .next(alert); });
      this.hubConnection.on('AlertDeleted' , (id   : string  ) => { this.alertDeletedSource .next(id   ); });

      //CRN_20260511 - Advisories (with _Updated_ variant)
      this.hubConnection.on('AdvisoryReceived', (advisory: AdvisoryDTO) => { this.advisoryReceivedSource.next(advisory); });
      this.hubConnection.on('AdvisoryUpdated' , (advisory: AdvisoryDTO) => { this.advisoryUpdatedSource .next(advisory); });
      this.hubConnection.on('AdvisoryDeleted' , (id      : string     ) => { this.advisoryDeletedSource .next(id      ); });

      //CRN_20260511 - Replace-on-receive: Reports/Summaries/PTAs/STA/CRL (no _Updated_)
      this.hubConnection.on('CityReportReceived'             , (report   : CityReportDTO                    ) => { this.cityReportReceivedSource      .next(report   ); });
      this.hubConnection.on('CityReportDeleted'              , (iataCode : string                           ) => { this.cityReportDeletedSource       .next(iataCode ); });
      this.hubConnection.on('CountryReportReceived'          , (report   : CountryReportDTO                 ) => { this.countryReportReceivedSource   .next(report   ); });
      this.hubConnection.on('CountryReportDeleted'           , (payload  : RisklineCompositeKeyDeletePayload) => { this.countryReportDeletedSource    .next(payload  ); });
      this.hubConnection.on('CountryRiskLevelReceived'       , (riskLevel: CountryRiskLevelDTO              ) => { this.countryRiskLevelReceivedSource.next(riskLevel); });
      this.hubConnection.on('CountryRiskLevelDeleted'        , (payload  : RisklineCompositeKeyDeletePayload) => { this.countryRiskLevelDeletedSource .next(payload  ); });
      this.hubConnection.on('PreTravelAdvisoryReceived'      , (pta      : PtaDTO                           ) => { this.ptaReceivedSource             .next(pta      ); });
      this.hubConnection.on('PreTravelAdvisoryDeleted'       , (payload  : RisklineCompositeKeyDeletePayload) => { this.ptaDeletedSource              .next(payload  ); });
      this.hubConnection.on('SpecificTravellerAdviceReceived', (sta      : SpecificTravellerAdviceDTO       ) => { this.staReceivedSource             .next(sta      ); });
      this.hubConnection.on('SpecificTravellerAdviceDeleted' , (isoCode  : string                           ) => { this.staDeletedSource              .next(isoCode  ); });
      this.hubConnection.on('SummaryReceived'                , (summary  : SummaryDTO                       ) => { this.summaryReceivedSource         .next(summary  ); });
      this.hubConnection.on('SummaryDeleted'                 , (payload  : RisklineCompositeKeyDeletePayload) => { this.summaryDeletedSource          .next(payload  ); });

      this.hubConnection.onreconnecting(() => { this.startPollFallback(); });
      this.hubConnection.onreconnected (() => { this.stopPollFallback (); });
      this.hubConnection.onclose       (() => { this.startPollFallback(); });

      this.hubConnection.start()
        .then (() => {
          this.isHubStarting=false;
          this.stopPollFallback();
        })
        .catch((error) => {
          this.isHubStarting=false;
          this.startPollFallback();
          this.exceptionsService.handleException(new Error('RisklineAlertsService.startHubConnection() - ' + (error?.message ?? error)));
        });
    }
    catch(e) {
      this.isHubStarting=false;
      this.startPollFallback();
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public stopHubConnection(): void {
    try {
      this.stopPollFallback();

      if (this.hubConnection==undefined)
        return;

      this.hubConnection.stop()
        .catch((error) => {
          this.exceptionsService.handleException(new Error('RisklineAlertsService.stopHubConnection() - ' + (error?.message ?? error)));
        });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public subscribeToAllAlerts(): void {
    try {
      if (this.hubConnection==undefined || this.hubConnection.state!=signalR.HubConnectionState.Connected)
        return;

      this.hubConnection.invoke('SubscribeToAllAlerts')
        .catch((error) => {
          this.exceptionsService.handleException(new Error('RisklineAlertsService.subscribeToAllAlerts() - ' + (error?.message ?? error)));
        });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public subscribeToCountry(isoCode: string): void {
    try {
      if (this.hubConnection==undefined || this.hubConnection.state!=signalR.HubConnectionState.Connected)
        return;

      this.hubConnection.invoke('SubscribeToCountry', isoCode)
        .catch((error) => {
          this.exceptionsService.handleException(new Error('RisklineAlertsService.subscribeToCountry() - ' + (error?.message ?? error)));
        });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public unsubscribeFromAllAlerts(): void {
    try {
      if (this.hubConnection==undefined || this.hubConnection.state!=signalR.HubConnectionState.Connected)
        return;

      this.hubConnection.invoke('UnsubscribeFromAllAlerts')
        .catch((error) => {
          this.exceptionsService.handleException(new Error('RisklineAlertsService.unsubscribeFromAllAlerts() - ' + (error?.message ?? error)));
        });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public unsubscribeFromCountry(isoCode: string): void {
    try {
      if (this.hubConnection==undefined || this.hubConnection.state!=signalR.HubConnectionState.Connected)
        return;

      this.hubConnection.invoke('UnsubscribeFromCountry', isoCode)
        .catch((error) => {
          this.exceptionsService.handleException(new Error('RisklineAlertsService.unsubscribeFromCountry() - ' + (error?.message ?? error)));
        });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
